import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SchemaOrg from '@/components/SchemaOrg';
import { FileText, ArrowRight, CheckCircle, Clock, Home as HomeIcon, Building, Camera, Users, Ruler, PenTool, Images, CreditCard } from 'lucide-react';
import { PLAN_MEASURE_APP_URL } from '@/config/site';
import { API_URL } from '@/utils/api';
import { trackHpdDrawCtaClicked } from '@/lib/analytics';

const PlanMeasureLink = ({ children, className, ...rest }) => {
  const external = PLAN_MEASURE_APP_URL.startsWith('http');
  if (external) {
    return <a href={PLAN_MEASURE_APP_URL} target="_blank" rel="noopener noreferrer" className={className} {...rest}>{children}</a>;
  }
  return <Link to={PLAN_MEASURE_APP_URL} className={className} {...rest}>{children}</Link>;
};

const Home = () => {
  const [galleryPreview, setGalleryPreview] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/gallery-submissions/approved`)
      .then((res) => setGalleryPreview((res.data.items || []).slice(0, 3)))
      .catch(() => setGalleryPreview([]));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <SEO 
        pageId="home"
        defaultTitle="Floor Plan Creator & Measurement Tool | House Plan Direct"
        defaultDescription="Create a professional floor plan online for free — draw from scratch or measure an existing plan, then present a polished layout. Free to create and edit. Pay £2.99 only to unlock the finished project."
        aiSummary="House Plan Direct is a browser-based floor-plan toolkit built around three steps: Create (draw a floor plan from scratch to exact measurements), Measure (upload and calibrate an existing PDF or image plan), and Present (arrange a polished marketing floor plan with room areas and GIA). Creating and editing a project is free; a finished project can be unlocked for £2.99. A separate managed 'We draw your plan' service, where House Plan Direct's team redraws a plan for you from £40, remains available as an alternative."
        keywords="floor plan creator, online floor plan creator, plan measurement tool, measure floor plan online, create floor plan online, draw floor plan online, estate agent floor plans"
      />
      <SchemaOrg />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-4xl mx-auto space-y-5">
            <p className="text-sm font-semibold uppercase tracking-widest text-emerald-600" data-testid="hero-kicker">
              Create. Measure. Present. Export.
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Professional Property Floor Plans<br />
              <span className="text-blue-600">and Plan Measurement Tools</span>
            </h1>
            <p className="text-xl sm:text-2xl font-semibold text-emerald-700 max-w-3xl mx-auto">
              Create a professional floor plan yourself — in your browser, in minutes.
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Draw from measurements or upload an existing plan. Create and edit for free, then
              unlock your finished project for just £2.99.
            </p>
          </div>

          {/* Primary action — HPD Draw is the dominant journey */}
          <div className="mt-10 flex flex-col items-center gap-4" data-testid="hero-primary-cta-block">
            <PlanMeasureLink onClick={() => trackHpdDrawCtaClicked('homepage_hero', 'Start HPD Draw Free', 'home', 'general')}>
              <Button size="lg" className="h-14 px-10 text-lg bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20" data-testid="hero-open-plan-measure-btn">
                <Ruler className="mr-2 h-5 w-5" /> Start HPD Draw Free
              </Button>
            </PlanMeasureLink>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-gray-600" data-testid="hero-reassurance-row">
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-600" /> No account required</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-600" /> Free to create</span>
              <span className="flex items-center gap-1.5" data-testid="badge-free-to-create"><CheckCircle className="h-4 w-4 text-emerald-600" /> £2.99 only when you export</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-600" /> Unlimited amendments after unlock</span>
            </div>
            <Link
              to="/order"
              className="text-sm text-gray-500 hover:text-blue-600 underline"
              data-testid="hero-managed-service-link"
              onClick={() => trackHpdDrawCtaClicked('homepage_hero', 'Prefer us to do it for you', 'home', 'general')}
            >
              Prefer us to do it for you? Floor plan service from £40
            </Link>
          </div>
        </div>
      </section>

      {/* Two entry routes — both are HPD Draw, just two starting points */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100" data-testid="entry-routes-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-2">How would you like to start?</h2>
          <p className="text-center text-gray-600 mb-10">Both routes are part of HPD Draw — pick whichever matches what you already have.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-7 hover:border-emerald-400 hover:shadow-lg transition" data-testid="entry-route-draw">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                <PenTool className="h-5 w-5 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">I have measurements</h3>
              <p className="text-gray-600 mb-5">Draw your floor plan from wall lengths and room dimensions.</p>
              <Link
                to="/floor-plan-creator"
                onClick={() => trackHpdDrawCtaClicked('homepage_route_draw', 'Start drawing', 'home', 'draw')}
              >
                <Button size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="entry-route-draw-btn">
                  Start drawing <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-7 hover:border-emerald-400 hover:shadow-lg transition" data-testid="entry-route-measure">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                <Ruler className="h-5 w-5 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">I already have a plan</h3>
              <p className="text-gray-600 mb-5">Upload a PDF or image, calibrate it and measure directly from the drawing.</p>
              <Link
                to="/measurement-tool"
                onClick={() => trackHpdDrawCtaClicked('homepage_route_measure', 'Upload & measure', 'home', 'measure')}
              >
                <Button size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="entry-route-measure-btn">
                  Upload &amp; measure <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <p className="text-center text-sm text-gray-400 mt-6">
            Not ready to start?{' '}
            <Link to="/hpd-draw-gallery" className="underline hover:text-blue-600" data-testid="entry-routes-try-example-link">
              See real finished examples first
            </Link>{' '}(a gallery of finished plans — not an interactive demo).
          </p>
        </div>
      </section>

      {/* Create, Measure, Present, Export — the DIY workflow */}
      <section id="create-measure-present" className="py-16 px-4 sm:px-6 lg:px-8 bg-emerald-50/60" data-testid="diy-workflow-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">Create, Measure, Present, Export</h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">One product, four steps — free to build, £2.99 to unlock the finished project.</p>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="rounded-xl bg-white border border-emerald-100 p-6 shadow-sm" data-testid="diy-step-create">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3 text-emerald-700 font-bold text-sm">1</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Create</h3>
              <p className="text-sm text-gray-600">Draw walls, rooms, doors, windows and fixtures in the <Link to="/floor-plan-creator" className="text-emerald-700 underline hover:text-emerald-800" data-testid="home-link-floor-plan-creator">online floor plan creator</Link>.</p>
            </div>
            <div className="rounded-xl bg-white border border-emerald-100 p-6 shadow-sm" data-testid="diy-step-measure">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3 text-emerald-700 font-bold text-sm">2</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Measure</h3>
              <p className="text-sm text-gray-600">Enter dimensions, or <Link to="/measurement-tool" className="text-emerald-700 underline hover:text-emerald-800" data-testid="home-link-measurement-tool">calibrate an uploaded plan</Link> and measure directly from it.</p>
            </div>
            <div className="rounded-xl bg-white border border-emerald-100 p-6 shadow-sm" data-testid="diy-step-present">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3 text-emerald-700 font-bold text-sm">3</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Present</h3>
              <p className="text-sm text-gray-600">Arrange your finished floor plan for a professional, presentation-ready result.</p>
            </div>
            <div className="rounded-xl bg-white border border-emerald-100 p-6 shadow-sm" data-testid="diy-step-export">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3 text-emerald-700 font-bold text-sm">4</div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Export</h3>
              <p className="text-sm text-gray-600">Unlock the project once for £2.99 and export your finished plan — unlimited re-exports after.</p>
            </div>
          </div>
          <div className="text-center mt-10">
            <PlanMeasureLink onClick={() => trackHpdDrawCtaClicked('homepage_process', 'Start HPD Draw Free', 'home', 'general')}>
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="diy-workflow-cta">
                Start HPD Draw Free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </PlanMeasureLink>
          </div>
        </div>
      </section>

      {/* Made with HousePlanDirect — HPD Draw Gallery teaser */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100" data-testid="gallery-teaser-section">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wide">Made with HousePlanDirect</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">What you can create with HPD Draw</h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            Real plans voluntarily shared by users after exporting their own floor plan. We don't normally store your drawings — this is entirely opt-in.
          </p>
          {galleryPreview.length > 0 && (
            <div className="mt-8 grid grid-cols-3 gap-4">
              {galleryPreview.map((item) => (
                <img
                  key={item.id}
                  src={`${API_URL}/gallery-submissions/image/${item.filename}`}
                  alt={item.admin_alt_text || item.admin_title || item.title || 'DIY floor plan made with HousePlanDirect'}
                  className="w-full h-28 sm:h-36 object-contain bg-gray-50 rounded-lg border border-gray-200"
                  loading="lazy"
                  width={240}
                  height={160}
                />
              ))}
            </div>
          )}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <PlanMeasureLink onClick={() => trackHpdDrawCtaClicked('homepage_examples', 'Create your own floor plan', 'home', 'general')}>
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="gallery-teaser-create-cta">
                Create your own floor plan <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </PlanMeasureLink>
            <Link to="/hpd-draw-gallery">
              <Button variant="outline" size="lg" data-testid="gallery-teaser-cta">
                <Images className="mr-2 h-4 w-4" /> See the full gallery
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Choose the right route */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="compare-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">Choose the right route</h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">Not sure which service fits your job? Here&apos;s how they differ.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-6">
              <h3 className="font-bold text-lg text-gray-900 mb-3">Plan Measure — do it yourself</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• You work directly in the browser</li>
                <li>• Create, measure and present your own plan</li>
                <li>• Free to create and edit — £2.99 to unlock the finished project</li>
                <li>• Export your own working drawings</li>
                <li>• Best for surveyors, assessors and property professionals</li>
              </ul>
              <p className="text-sm text-gray-600 mt-4"><Link to="/floor-plan-creator" className="text-emerald-700 underline hover:text-emerald-800">Open the Floor Plan Creator</Link> or <Link to="/measurement-tool" className="text-emerald-700 underline hover:text-emerald-800">measure an existing plan</Link>.</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-6">
              <h3 className="font-bold text-lg text-gray-900 mb-3">Professional Floor Plan Service</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• You provide the source material</li>
                <li>• We redraw and prepare the plan</li>
                <li>• Best for polished marketing outputs</li>
                <li>• Suitable for busy agents, landlords and surveyors</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="audience-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Trusted by Property Professionals</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">We provide floor plans for property marketing across the UK</p>
          <div className="grid md:grid-cols-5 gap-6">
            {[
              { icon: Building, title: "Estate Agents", desc: "Rightmove & Zoopla ready" },
              { icon: HomeIcon, title: "Property Sellers", desc: "House sale floor plans" },
              { icon: Users, title: "Landlords", desc: "Rental property plans" },
              { icon: Camera, title: "Photographers", desc: "Property photography add-on" },
              { icon: FileText, title: "EPC Assessors", desc: "EPC compliant floor plans" }
            ].map((item) => (
              <div key={item.title} className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-md transition-shadow">
                <item.icon className="h-10 w-10 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Managed service — secondary fallback, deliberately low-key vs the HPD Draw journey above */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-gray-100" data-testid="process-section">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl border border-gray-200 bg-white p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Prefer us to draw it for you?</h2>
            <p className="text-gray-600 mb-6">
              Send us your sketch or measurements and we can create the finished floor plan for you from £40.
              JPEG, PDF and DWG (CAD) included, with 24–48 hour turnaround.
            </p>
            <ol className="space-y-2 text-sm text-gray-600 mb-6">
              <li><span className="font-semibold text-gray-800">1.</span> Upload your hand-drawn sketch, photo or old plan</li>
              <li><span className="font-semibold text-gray-800">2.</span> Our team redraws it into a professional, listing-ready floor plan</li>
              <li><span className="font-semibold text-gray-800">3.</span> Receive your plan in JPEG, PDF &amp; DWG within 24–48 hours</li>
            </ol>
            <Link
              to="/order"
              onClick={() => trackHpdDrawCtaClicked('managed_service_fallback', 'View done-for-you service', 'home', 'general')}
            >
              <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="managed-service-cta">
                View done-for-you service <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose House Plan Direct?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4" data-testid="benefit-rightmove">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Rightmove & Zoopla Ready</h3>
              <p className="text-gray-600">
                Floor plans formatted perfectly for UK property portals. Increase listing engagement and attract more buyers.
              </p>
            </div>
            <div className="text-center space-y-4" data-testid="benefit-affordable">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <CreditCard className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Affordable Fixed Pricing</h3>
              <p className="text-gray-600">
                Starting at just £40. No hidden fees. Get an instant quote with our pricing calculator.
              </p>
            </div>
            <div className="text-center space-y-4" data-testid="benefit-fast">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                <Clock className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">24-48 Hour Turnaround</h3>
              <p className="text-gray-600">
                Fast delivery when you need it. Perfect for urgent property listings and quick sales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Links */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Floor Plan Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Estate Agent Floor Plans", desc: "Professional floor plans for property listings", link: "/estate-agent-floor-plans" },
              { title: "Rightmove Floor Plans", desc: "Formatted for UK property portals", link: "/rightmove-floor-plan-service" },
              { title: "Landlord Floor Plans", desc: "Rental property marketing plans", link: "/landlord-floor-plans" },
              { title: "Property Marketing Plans", desc: "Brochure-ready floor plans", link: "/property-marketing-floor-plans" },
              { title: "EPC Floor Plans", desc: "Compliant plans for EPC assessors", link: "/epc-floor-plans" },
              { title: "Floor Plans from Sketch", desc: "Convert any sketch to professional plan", link: "/floor-plan-from-sketch" }
            ].map((service) => (
              <Link key={service.link} to={service.link} className="block p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{service.desc}</p>
                <span className="text-blue-600 text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI/GEO Summary Block */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About House Plan Direct</h2>
            <p className="text-gray-700 mb-4">
              <strong>HousePlanDirect lets you create, measure and present your own professional floor plan online — free to build and edit, with a one-off £2.99 unlock for the finished, watermark-free project.</strong> Prefer not to draw it yourself? HousePlanDirect also runs a UK managed service that converts sketches, photos or old plans into professional property marketing floor plans suitable for estate agent listings on Rightmove and Zoopla.
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li>• <strong>DIY toolkit:</strong> Free to create and edit online; £2.99 to unlock the finished project</li>
              <li>• <strong>Managed service price:</strong> Floor plans from £40</li>
              <li>• <strong>Managed service turnaround:</strong> 24-48 hours</li>
              <li>• <strong>Formats:</strong> JPEG, PDF, and DWG</li>
              <li>• <strong>Service area:</strong> United Kingdom</li>
              <li>• <strong>Best for:</strong> Estate agents, landlords, property sellers, photographers, EPC assessors</li>
            </ul>
            <p className="text-gray-700">
              Use the free online tools to create, measure and present your own floor plan, or upload your hand-drawn sketch, photo, or old floor plan and let our team prepare a professional, listing-ready floor plan suitable for property marketing, Rightmove, Zoopla, and property brochures.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              {
                q: "Is it free to create my own floor plan?",
                a: "Yes. The Floor Plan Creator and Plan Measure are both free to use for creating, measuring and editing. A one-off £2.99 unlocks the finished, watermark-free project — no subscription."
              },
              {
                q: "Can I measure an existing floor plan online?",
                a: "Yes. Upload a PDF or image, calibrate it against one known dimension, then measure lengths, areas and rooms for free directly in your browser with Plan Measure."
              },
              {
                q: "What is a property marketing floor plan?",
                a: "A property marketing floor plan is a professional 2D drawing showing the layout of a property. It's used by estate agents on Rightmove, Zoopla, and in property brochures to help buyers visualise the space and room layout."
              },
              {
                q: "Do estate agents need floor plans for listings?",
                a: "Yes, floor plans significantly increase buyer engagement. Properties with floor plans receive more enquiries and viewings. Rightmove and Zoopla both support and recommend floor plans for property listings."
              },
              {
                q: "How much does a property floor plan cost in the UK?",
                a: "At House Plan Direct, floor plans start from just £40 for up to 3 rooms. Additional rooms cost £5 each. This includes delivery in JPEG, PDF, and DWG formats within 24-48 hours."
              },
              {
                q: "Can a sketch be converted into a professional floor plan?",
                a: "Yes! Simply upload your hand-drawn sketch, a photo of your floor plan, or an old plan. We'll convert it into a professional, listing-ready floor plan suitable for Rightmove, Zoopla, and property brochures."
              },
              {
                q: "How long does it take to create a floor plan?",
                a: "Standard turnaround is 24-48 hours. We offer fast delivery for urgent property listings and quick house sales."
              }
            ].map((faq) => (
              <div key={faq.q} className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/faqs">
              <Button variant="outline">View All FAQs <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-bold">
            Ready to Get Your Professional Floor Plan?
          </h2>
          <p className="text-lg text-blue-100">
            Create your own floor plan online in minutes — free to build and edit, £2.99 to unlock the finished project.
          </p>
          <PlanMeasureLink onClick={() => trackHpdDrawCtaClicked('homepage_process', 'Start HPD Draw Free', 'home', 'general')}>
            <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8" data-testid="cta-start-btn">
              Start HPD Draw Free
            </Button>
          </PlanMeasureLink>
          <p className="text-sm text-blue-100">
            Prefer us to do it for you?{' '}
            <Link to="/order" className="underline hover:text-white" data-testid="cta-order-instead-link">We draw your plan</Link>
          </p>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto prose prose-gray">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">UK Property Floor Plan Service</h2>
          <p className="text-gray-600 mb-8">
            House Plan Direct provides fast, affordable floor plans for property marketing across the United Kingdom. 
            Whether you're an estate agent needing floor plans for Rightmove listings, a landlord marketing a rental property, 
            or a homeowner selling your house, we convert your sketch into a professional floor plan within 24-48 hours.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">Estate Agent Floor Plans for Rightmove & Zoopla</h3>
          <p className="text-gray-600 mb-8">
            Properties with floor plans receive significantly more enquiries than those without. Our floor plans are 
            formatted perfectly for UK property portals including Rightmove, Zoopla, OnTheMarket, and PrimeLocation. 
            Help your buyers visualise the space and generate more viewings with professional floor plans.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">Convert Sketch to Estate Agent Floor Plan</h3>
          <p className="text-gray-600 mb-8">
            Don't have a professional floor plan? No problem. Simply measure your property, create a rough sketch, 
            and upload it to our service. We'll convert your sketch into a clean, professional floor plan suitable 
            for property listings, marketing brochures, and EPC assessments.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">Floor Plans for House Sales UK</h3>
          <p className="text-gray-600 mb-8">
            Selling your home? A floor plan helps buyers understand the layout before viewing, saving time and 
            attracting serious buyers. Our floor plans are used by homeowners, estate agents, and property 
            photographers across England, Wales, Scotland, and Northern Ireland.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
